import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';


const fadeUp = { hidden: { opacity: 0, y: 28 }, show: { opacity: 1, y: 0, transition: { duration: 0.6 } } };
const stagger = { show: { transition: { staggerChildren: 0.1 } } };

/* ── Design tokens ─────────────────────────────────────────────────── */
const GOLD   = '#C9A227';
const GOLD_S = '#E6D39A';
const NAVY   = '#0F172A';
const NAVY2  = '#1E293B';
const BODY   = '#475569';
const BG     = '#F8FAFC';

/* ── Shared primitives ─────────────────────────────────────────────── */
const SLabel = ({ children, light }) => (
  <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '0.68rem', fontWeight: 600,
    letterSpacing: '0.22em', textTransform: 'uppercase',
    color: light ? GOLD_S : GOLD, marginBottom: '0.75rem' }}>
    {children}
  </p>
);

const STitle = ({ children, light, center }) => (
  <h2 style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 'clamp(2rem, 4vw, 3.2rem)',
    fontWeight: 700, lineHeight: 1.2, color: light ? '#fff' : NAVY,
    marginBottom: '1rem', textAlign: center ? 'center' : 'left' }}>
    {children}
  </h2>
);

const GoldBar = ({ center }) => (
  <div style={{ width: '52px', height: '3px', borderRadius: '2px',
    background: `linear-gradient(90deg, ${GOLD}, ${GOLD_S})`,
    margin: center ? '0 auto 2rem' : '0 0 2rem' }} />
);

const GoldDivider = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '0 auto', maxWidth: '320px' }}>
    <div style={{ flex: 1, height: '1px', background: `linear-gradient(90deg, transparent, rgba(201,162,39,0.4))` }} />
    <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: GOLD }} />
    <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: `rgba(201,162,39,0.4)` }} />
    <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: GOLD }} />
    <div style={{ flex: 1, height: '1px', background: `linear-gradient(90deg, rgba(201,162,39,0.4), transparent)` }} />
  </div>
);

/* ── SVG Icons ─────────────────────────────────────────────────────── */
const Icon = ({ d, size = 26, viewBox = '0 0 24 24', children, strokeWidth = 1.6 }) => (
  <svg width={size} height={size} viewBox={viewBox} fill="none" stroke="currentColor"
    strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
    {d ? <path d={d} /> : children}
  </svg>
);

const Icons = {
  Landmark: () => <Icon><path d="M3 22h18M3 10h18M12 2l9 8H3l9-8zM5 10v12M9 10v12M15 10v12M19 10v12"/></Icon>,
  HeartPulse: () => <Icon><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/><path d="M3.22 12H9.5l.5-1 2 4.5 2-7 1.5 3.5h5.27"/></Icon>,
  Scale: () => <Icon><path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1ZM2 16l3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="M7 21h10M12 3v18M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2"/></Icon>,
  Laptop: () => <Icon><path d="M20 16V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v9m14 0H4m16 0 1.28 2.55a1 1 0 0 1-.9 1.45H3.62a1 1 0 0 1-.9-1.45L4 16"/></Icon>,
  Briefcase: () => <Icon><path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/><rect width="20" height="14" x="2" y="6" rx="2"/></Icon>,
  GraduationCap: () => <Icon><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></Icon>,
  Target: () => <Icon><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></Icon>,
  Sparkles: () => <Icon><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/><path d="M5 3v4M19 17v4M3 5h4M17 19h4"/></Icon>,
  Shield: () => <Icon><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></Icon>,
  Users: () => <Icon><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></Icon>,
  BookOpen: () => <Icon><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></Icon>,
  HeartHandshake: () => <Icon><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/><path d="M12 5 9.04 7.96a2.17 2.17 0 0 0 0 3.08v0c.82.82 2.13.85 3 .07l2.07-1.9a2.82 2.82 0 0 1 3.79 0l2.96 2.66"/><path d="m18 15-2-2"/><path d="m15 18-2-2"/></Icon>,
  HandCoins: () => <Icon><path d="M11 15h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 17"/><path d="m7 21 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9"/><path d="m2 16 6 6"/><circle cx="16" cy="9" r="2.9"/><circle cx="6" cy="5" r="3"/></Icon>,
  Building2: () => <Icon><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></Icon>,
  ScrollText: () => <Icon><path d="M8 21h12a2 2 0 0 0 2-2v-2H10v2a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v3h4"/><path d="M19 3H4.5"/><path d="M15 13H9"/><path d="M15 17H9"/><path d="M10 9H9"/></Icon>,
  Flame: () => <Icon><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></Icon>,
  Globe: () => <Icon><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></Icon>,
};

/* ── Icon Container ────────────────────────────────────────────────── */
const IconBox = ({ icon: IconComp, size = 48, boxSize = 72 }) => (
  <div style={{ width: boxSize, height: boxSize, borderRadius: '16px', flexShrink: 0,
    background: 'rgba(201,162,39,0.08)', border: '1px solid rgba(201,162,39,0.18)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: GOLD,
    transition: 'transform 0.3s, box-shadow 0.3s' }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = `0 0 20px rgba(201,162,39,0.25)`; }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}>
    <IconComp size={size} />
  </div>
);

/* ── Premium Card ──────────────────────────────────────────────────── */
const Card = ({ children, style }) => (
  <div style={{ background: '#fff', borderRadius: '24px', border: '1px solid rgba(15,23,42,0.08)',
    boxShadow: '0 4px 24px rgba(15,23,42,0.06)', transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    ...style }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 16px 48px rgba(15,23,42,0.12)'; }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(15,23,42,0.06)'; }}>
    {children}
  </div>
);

const AboutPage = () => (
  <div style={{ background: BG, color: NAVY }} className="min-h-screen">

    {/* ══ HERO ══════════════════════════════════════════════════════ */}
    <section className="vrv-hero vrv-hero-about" style={{ paddingTop: '3rem', paddingBottom: '3rem' }}>
      <div className="vrv-container">
        <div className="relative z-10 flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div style={{ marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '40px', height: '1px', background: 'rgba(212,175,55,0.4)' }} />
              <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#D4AF37' }} />
              <div style={{ width: '40px', height: '1px', background: 'rgba(212,175,55,0.4)' }} />
            </div>
            <blockquote style={{
              fontFamily: '"Cormorant Garamond", "Playfair Display", serif',
              fontSize: 'clamp(1rem, 2vw, 1.55rem)',
              fontWeight: 600,
              lineHeight: 2,
              color: '#D4AF37',
              textShadow: '0 0 40px rgba(212,175,55,0.35)',
              letterSpacing: '0.04em',
              fontStyle: 'italic',
            }}>
              Śrī Lakṣmīvallabhārambhāṁ Vikhano Muni Madhyamām ।<br />
              Asmad Ācārya Paryantāṁ Vande Guru Paramparām ‖
            </blockquote>
            <div style={{ marginTop: '1.25rem', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '40px', height: '1px', background: 'rgba(212,175,55,0.4)' }} />
              <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#D4AF37' }} />
              <div style={{ width: '40px', height: '1px', background: 'rgba(212,175,55,0.4)' }} />
            </div>
          </div>
          <div className="hidden md:flex flex-shrink-0" style={{ marginRight: '4.7rem', alignSelf: 'flex-start', position: 'relative', top: '0.2rem' }}>
            <img src="/images/VRV.png" alt="VRV" className="h-64 w-64 object-contain"
              style={{ filter: 'drop-shadow(0 0 56px rgba(212,175,55,0.7))' }} />
          </div>
        </div>
      </div>
    </section>

    {/* ══ SECTION 1 — WHO WE ARE ════════════════════════════════════ */}
    <section style={{ background: '#fff', padding: '120px 0' }}>
      <div className="vrv-container">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
          <div className="flex flex-col lg:flex-row gap-16 items-center">

            {/* Left — ServingVishnu image */}
            <motion.div variants={fadeUp} className="lg:w-5/12 w-full flex-shrink-0">
              <div style={{ borderRadius: '28px', overflow: 'hidden', border: `1px solid rgba(201,162,39,0.2)`, boxShadow: '0 24px 64px rgba(15,23,42,0.18)', lineHeight: 0 }}>
                <img
                  src="/images/ServingVishnu.png"
                  alt="Serving Lord Vishnu across every walk of life"
                  style={{ width: '100%', display: 'block', objectFit: 'cover' }}
                />
              </div>
            </motion.div>


            {/* Right — text */}
            <motion.div variants={fadeUp} className="lg:w-7/12 w-full">
              <SLabel>Our Identity</SLabel>
              <STitle>Who We Are23</STitle>
              <GoldBar />
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '1.05rem', color: BODY, lineHeight: 1.85, marginBottom: '1.5rem' }}>
                We are a worldwide community organization dedicated to the <strong style={{ color: NAVY }}>Vaikhanasa sect</strong> — one of the most ancient and revered traditions in Vaishnavism, established by Maharshi Vikhanasa as ordained by Lord Vishnu himself.
              </p>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '1.05rem', color: BODY, lineHeight: 1.85, marginBottom: '2rem' }}>
                Our members span every walk of life — priests and scholars preserving sacred rituals, professionals serving society with excellence, and students carrying the flame of tradition into the future.
              </p>
              <div style={{ borderLeft: `3px solid ${GOLD}`, paddingLeft: '1.5rem', marginBottom: '2rem' }}>
                <p style={{ fontFamily: '"Playfair Display", serif', fontSize: '1.1rem', color: NAVY, fontStyle: 'italic', lineHeight: 1.7 }}>
                  "Though our professions differ, we remain united by the sacred Vaikhanasa tradition established by Maharshi Vikhanasa."
                </p>
              </div>

            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>

    {/* ══ SECTION 2 — MISSION & VISION ═════════════════════════════ */}
    <section style={{ background: BG, padding: '120px 0' }}>
      <div className="vrv-container">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center" style={{ marginBottom: '3.5rem' }}>
            <SLabel>Our Purpose</SLabel>
            <STitle center>Mission &amp; Vision</STitle>
            <GoldBar center />
          </motion.div>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Mission */}
            <motion.div variants={fadeUp}>
              <Card style={{ height: '100%', overflow: 'hidden' }}>
                <div style={{ height: '4px', background: `linear-gradient(90deg, ${NAVY}, ${NAVY2})` }} />
                <div style={{ padding: '2.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.25rem', marginBottom: '1.5rem' }}>
                    <IconBox icon={Icons.Target} size={26} boxSize={64} />
                    <div>
                      <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: BODY, marginBottom: '0.3rem' }}>Our Mission</p>
                      <h3 style={{ fontFamily: '"Playfair Display", serif', fontSize: '1.6rem', fontWeight: 700, color: NAVY, lineHeight: 1.2 }}>Unite &amp; Empower</h3>
                    </div>
                  </div>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '1rem', color: BODY, lineHeight: 1.85 }}>
                    To unite the global Vaikhanasa community by fostering spiritual growth, preserving ancient traditions, and creating a platform for cultural exchange, education, and mutual support across generations.
                  </p>
                </div>
              </Card>
            </motion.div>
            {/* Vision */}
            <motion.div variants={fadeUp}>
              <Card style={{ height: '100%', overflow: 'hidden' }}>
                <div style={{ height: '4px', background: `linear-gradient(90deg, ${GOLD}, ${GOLD_S})` }} />
                <div style={{ padding: '2.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.25rem', marginBottom: '1.5rem' }}>
                    <IconBox icon={Icons.Sparkles} size={26} boxSize={64} />
                    <div>
                      <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: BODY, marginBottom: '0.3rem' }}>Our Vision</p>
                      <h3 style={{ fontFamily: '"Playfair Display", serif', fontSize: '1.6rem', fontWeight: 700, color: NAVY, lineHeight: 1.2 }}>Thrive &amp; Connect</h3>
                    </div>
                  </div>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '1rem', color: BODY, lineHeight: 1.85 }}>
                    A thriving, connected Vaikhanasa community worldwide — rooted in Vedic wisdom, empowered by modern technology, and dedicated to the service of Lord Vishnu and humanity.
                  </p>
                </div>
              </Card>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>

    {/* ══ SECTION 3 — CORE VALUES ══════════════════════════════════ */}
    <section style={{ background: '#fff', padding: '120px 0' }}>
      <div className="vrv-container">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center" style={{ marginBottom: '3.5rem' }}>
            <SLabel>Our Values</SLabel>
            <STitle center>What Drives Us</STitle>
            <GoldBar center />
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '1rem', color: BODY, maxWidth: '480px', margin: '0 auto', lineHeight: 1.8 }}>
              Four pillars that define the Vaikhanasa way of life — timeless, purposeful, and deeply rooted.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Icons.Shield, title: 'Spirituality', desc: 'Rooted in Vedic rituals, Agamic wisdom, and unwavering devotion to Lord Vishnu.' },
              { icon: Icons.Users, title: 'Unity', desc: 'Bringing together Vaikhanasa families, priests, and communities worldwide.' },
              { icon: Icons.BookOpen, title: 'Knowledge', desc: 'Preserving, sharing, and teaching the sacred Agama Shastra across generations.' },
              { icon: Icons.HeartHandshake, title: 'Service', desc: 'Serving the community through charity, welfare, and compassionate action.' },
            ].map(({ icon: Ic, title, desc }, i) => (
              <motion.div key={i} variants={fadeUp}>
                <Card style={{ padding: '2.25rem', textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.25rem' }}>
                    <IconBox icon={Ic} size={28} boxSize={72} />
                  </div>
                  <h3 style={{ fontFamily: '"Playfair Display", serif', fontSize: '1.35rem', fontWeight: 700, color: NAVY, marginBottom: '0.75rem' }}>{title}</h3>
                  <div style={{ width: '32px', height: '2px', background: GOLD, margin: '0 auto 0.85rem', borderRadius: '2px' }} />
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.9rem', color: BODY, lineHeight: 1.75 }}>{desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>

    {/* ══ SECTION 4 — COMMUNITY INITIATIVES ═══════════════════════ */}
    <section style={{ background: BG, padding: '120px 0' }}>
      <div className="vrv-container">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center" style={{ marginBottom: '3.5rem' }}>
            <SLabel>Our Community</SLabel>
            <STitle center>Community Initiatives</STitle>
            <GoldBar center />
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '1rem', color: BODY, maxWidth: '480px', margin: '0 auto', lineHeight: 1.8 }}>
              Purposeful programs that strengthen our community and honour our tradition.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Icons.HandCoins, title: 'Koustubham', desc: 'A monthly pension scheme providing financial assistance to elderly and retired Vaikhanasa priests, ensuring dignity and stability.' },
              { icon: Icons.GraduationCap, title: 'Gnyanadha Scheme', desc: 'Education assistance program providing scholarships and financial support to deserving students from the Vaikhanasa community.' },
              { icon: Icons.Building2, title: 'Temple Heritage Support', desc: 'Preserving Vaikhanasa temples, sacred rituals, and Agamic traditions for future generations.' },
              { icon: Icons.HeartHandshake, title: 'Community Welfare', desc: 'Supporting families and community members with compassion and care during times of need.' },
            ].map(({ icon: Ic, title, desc }, i) => (
              <motion.div key={i} variants={fadeUp} style={{ height: '100%' }}>
                <Card style={{ padding: '2.25rem', height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ marginBottom: '1.25rem' }}>
                    <IconBox icon={Ic} size={26} boxSize={64} />
                  </div>
                  <h3 style={{ fontFamily: '"Playfair Display", serif', fontSize: '1.25rem', fontWeight: 700, color: NAVY, marginBottom: '0.75rem' }}>{title}</h3>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.9rem', color: BODY, lineHeight: 1.75, flex: 1 }}>{desc}</p>
                  <div style={{ width: '32px', height: '2px', background: `rgba(201,162,39,0.35)`, marginTop: '1.5rem', borderRadius: '2px' }} />
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>

    {/* ══ SECTION 5 — FOUNDERS ══════════════════════════════════════ */}
    <section style={{ background: '#fff', padding: '120px 0' }}>
      <div className="vrv-container">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center" style={{ marginBottom: '3.5rem' }}>
            <SLabel>Our Founders</SLabel>
            <STitle center>The People Behind VRV</STitle>
            <GoldBar center />
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '1rem', color: BODY, maxWidth: '480px', margin: '0 auto', lineHeight: 1.8 }}>
              Dedicated individuals who established and continue to guide the Vaikhanasa Raksha Vahini mission.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { img: null, name: 'Founder Name', place: 'City, Country', occupation: 'Temple Priest & Scholar' },
              { img: null, name: 'Founder Name', place: 'City, Country', occupation: 'Community Leader' },
              { img: null, name: 'Founder Name', place: 'City, Country', occupation: 'Vedic Scholar' },
              { img: null, name: 'Founder Name', place: 'City, Country', occupation: 'Trustee & Advisor' },
            ].map(({ img, name, place, occupation }, i) => (
              <motion.div key={i} variants={fadeUp}>
                <div style={{ background: '#fff', borderRadius: '24px', border: '1px solid rgba(15,23,42,0.08)', boxShadow: '0 4px 24px rgba(15,23,42,0.06)', overflow: 'hidden', transition: 'transform 0.3s ease, box-shadow 0.3s ease', textAlign: 'center' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 16px 48px rgba(15,23,42,0.12)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(15,23,42,0.06)'; }}>
                  {/* Photo */}
                  <div style={{ width: '100%', aspectRatio: '1/1', background: 'linear-gradient(145deg, #0F172A, #1E293B)', position: 'relative', overflow: 'hidden' }}>
                    {img ? (
                      <img src={img} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'rgba(201,162,39,0.12)', border: '1px solid rgba(201,162,39,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(201,162,39,0.5)', marginBottom: '0.5rem' }}>
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                          </svg>
                        </div>
                        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.65rem', color: 'rgba(201,162,39,0.35)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Photo Coming Soon</p>
                      </div>
                    )}
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '40%', background: 'linear-gradient(to top, rgba(15,23,42,0.5), transparent)', pointerEvents: 'none' }} />
                  </div>
                  {/* Info */}
                  <div style={{ padding: '1.5rem 1.25rem' }}>
                    <h3 style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: '1.15rem', fontWeight: 700, color: NAVY, lineHeight: 1.3, marginBottom: '0.3rem' }}>{name}</h3>
                    <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', fontWeight: 600, color: GOLD, marginBottom: '0.35rem', letterSpacing: '0.02em' }}>{occupation}</p>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem' }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                      </svg>
                      <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', color: '#94A3B8' }}>{place}</p>
                    </div>
                  </div>
                  <div style={{ height: '3px', background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>

    {/* ══ SECTION 6 — FINAL CTA ════════════════════════════════════ */}
    <section style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY2} 100%)`, padding: '120px 0', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 50%, rgba(201,162,39,0.08) 0%, transparent 65%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', userSelect: 'none', opacity: 0.03, fontFamily: '"Playfair Display", serif', fontSize: '28rem', color: GOLD, lineHeight: 1 }}>ॐ</div>
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
        className="vrv-container text-center" style={{ position: 'relative', zIndex: 1, maxWidth: '720px', margin: '0 auto' }}>
        <SLabel light>Join Us</SLabel>
        <h2 style={{ fontFamily: '"Playfair Display", serif', fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', fontWeight: 700, lineHeight: 1.2, color: '#fff', marginBottom: '1.25rem' }}>
          Join the Global<br />
          <span style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD_S})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            Vaikhanasa Community
          </span>
        </h2>
        <div style={{ width: '52px', height: '2px', background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`, margin: '0 auto 1.5rem', borderRadius: '2px' }} />
        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '1rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.8, maxWidth: '480px', margin: '0 auto 1rem' }}>
          Preserving Heritage • Connecting Generations • Serving Society
        </p>
        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.95rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.8, maxWidth: '520px', margin: '0 auto 2.5rem' }}>
          Be part of a living tradition that spans continents — rooted in devotion, strengthened by community, and guided by the wisdom of Maharshi Vikhanasa.
        </p>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/signup"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
              background: `linear-gradient(135deg, ${GOLD}, ${GOLD_S})`, color: NAVY, padding: '0.9rem 2.25rem',
              borderRadius: '12px', textDecoration: 'none', transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: '0 4px 16px rgba(201,162,39,0.3)' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(201,162,39,0.45)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(201,162,39,0.3)'; }}>
            Join Us
          </Link>
          <Link to="/vaikhanasam"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
              background: 'transparent', color: `rgba(230,211,154,0.85)`, padding: '0.9rem 2.25rem',
              borderRadius: '12px', textDecoration: 'none', border: `1.5px solid rgba(201,162,39,0.4)`,
              transition: 'border-color 0.2s, color 0.2s, transform 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD_S; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(201,162,39,0.4)'; e.currentTarget.style.color = 'rgba(230,211,154,0.85)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
            Explore Vaikhanasam
          </Link>
        </div>

        {/* Existing quote preserved */}
        <div style={{ marginTop: '4rem' }}>
          <GoldDivider />
          <blockquote style={{ fontFamily: '"Playfair Display", serif', fontSize: 'clamp(1.1rem, 2vw, 1.5rem)', fontWeight: 600, fontStyle: 'italic', lineHeight: 1.65, color: 'rgba(255,255,255,0.75)', maxWidth: '640px', margin: '2rem auto 0' }}>
            "Vikhanas Maharshi established the path of eternal devotion —
            <span style={{ color: `rgba(201,162,39,0.85)` }}> we walk together to keep it alive."</span>
          </blockquote>
          <p style={{ marginTop: '1rem', fontFamily: 'Poppins, sans-serif', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: `rgba(201,162,39,0.4)` }}>— VRV Community</p>
        </div>
      </motion.div>
    </section>

  </div>
);

export default AboutPage;
